// --- Dependencies -------------------------------------------------------------------------------
import type { ReactNode } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    /** The tiles, in the order they should be read. `WorkCard`s and `FollowUpCard`s, mixed. */
    children: ReactNode;
    /** Nothing to draw. Said in one line rather than as a row of reassuring zeroes. */
    isEmpty: boolean;
};

/**
 * What is waiting on this company right now — the counts and the calls owed today, in **one** row.
 *
 * They were two sections and are one because they are one question. "Six installments are late" and
 * "ring Ahmed at four" are both answers to *what do I do now*, and splitting them put a heading
 * between a number and the work behind it.
 *
 * One **horizontal** row, scrolling, rather than a wrapping grid: the order matters here — latest
 * first — and a grid that reflows puts a different tile in the corner at every window width. Every
 * tile in it is the same size, which is what makes the row scannable instead of readable.
 *
 * A tile at zero is never drawn, so when everything is clear this is one line and no more.
 */
export default function WorkPanel({ children, isEmpty }: Props) {
    const t = useTranslation();

    return (
        <section className="work-panel">
            <h2 className="work-panel__title">
                <Icon name="calendar" size={16} />
                <span>{t.home.workTitle}</span>
            </h2>

            {isEmpty ? (
                <p className="work-panel__clear">
                    <Icon name="check" size={18} />
                    <span>{t.home.workClear}</span>
                </p>
            ) : (
                <div className="work-panel__row">{children}</div>
            )}
        </section>
    );
}
